import { Elements, PaymentElement, useElements } from '@stripe/react-stripe-js';
import { useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, type ExcludeOtherPaymentMethod } from '@/types/common';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { PayButton } from '../PaymentInputs/PayButton';
import { calculateTotalPrice } from '@/utils/pricing';
import type { StripePaymentResponse } from '@/types/payment';
import { STRIPE_APPEARANCE } from '@/constants/stripe';
import { Email } from '../Common/Email';
import { shouldHideEmail } from '@/utils/conditionalRendering';
import { useTranslate } from '@/hooks/useTranslate';
import { getLocale } from '@/utils/get-locale-util';
import type { Stripe, StripeElementLocale } from '@stripe/stripe-js';
import { stripeService } from '@/services/stripe';
import { Loader } from '../Common/Loader';
import { useStripeInitialize } from '@/hooks/useStripeInitialize';
import { isEmailValid } from '@/utils/email';

interface StripeGenericPayContentProps {
  paymentMethod: string;
}

const StripeGenericPayContent = ({ paymentMethod, stripe }: { paymentMethod: string; stripe: Stripe | undefined }) => {
  const elements = useElements();
  const [isValid, setIsValid] = useState(false);
  const [isCustomerAddressAndZipValid, setIsCustomerAddressAndZipValid] = useState(false);
  const {
    actions: { runPayment, setError, setIsFormValid },
    pricing,
    paymentMethodsSettings,
    paymentMethodSelected,
    otherMethodSelected,
    customer,
    featureFlags,
  } = useContext(CheckoutContext);
  const { t } = useTranslate();

  const providerSettings = useMemo(() => {
    const selectedPaymentMethod =
      paymentMethodSelected === PaymentMethodEnum.OTHER ? otherMethodSelected : paymentMethodSelected;
    return paymentMethodsSettings.find((paymentMethod) => paymentMethod.paymentMethodName === selectedPaymentMethod)!;
  }, [paymentMethodSelected, otherMethodSelected, paymentMethodsSettings]);

  useEffect(() => {
    setIsFormValid(isValid && isCustomerAddressAndZipValid && !!stripe && !!elements);
  }, [isValid, stripe, elements, setIsFormValid, isCustomerAddressAndZipValid]);

  const handlePaymentElementChange = (event: { complete: boolean }) => {
    setIsValid(event.complete && isEmailValid(customer?.email));
  };

  const handleAddressAndZipValidate = useCallback((isValid: boolean) => {
    setIsCustomerAddressAndZipValid(isValid);
  }, []);

  const handlePayment = async () => {
    if (!stripe || !elements || !providerSettings) {
      setError(t('somethingWentWrong'));
      return;
    }

    if (!isEmailValid(customer?.email)) {
      setError(t('somethingWentWrong'));
      return;
    }

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(t('somethingWentWrong'));
        return;
      }

      await runPayment<StripePaymentResponse>({ elements, type: paymentMethod });
    } catch {
      setError(t('somethingWentWrong'));
    }
  };

  const isPayButtonDisabled = !isEmailValid(customer?.email) || !isCustomerAddressAndZipValid || !stripe || !elements;
  return (
    <div className="flex flex-col gap-4">
      {!shouldHideEmail(featureFlags) && <Email />}
      <InputTaxComponent onValidate={handleAddressAndZipValidate} />
      <PaymentElement
        key={providerSettings.id}
        id={`${paymentMethod}-payment-element`}
        onChange={handlePaymentElementChange}
        options={{
          layout: 'auto',
          fields: {
            billingDetails: {
              email: 'never',
            },
          },
        }}
      />

      <PayButton price={calculateTotalPrice(pricing)} onClick={handlePayment} isDisabled={isPayButtonDisabled} />
    </div>
  );
};

export const StripeGenericPayForm = ({ paymentMethod }: StripeGenericPayContentProps) => {
  const { pricing, paymentMethodSelected, otherMethodSelected } = useContext(CheckoutContext);
  const { pspLocale } = getLocale();
  const selectedPaymentMethod =
    paymentMethodSelected === PaymentMethodEnum.OTHER ? otherMethodSelected : paymentMethodSelected;
  const { stripe, isLoading: isStripeLoading } = useStripeInitialize({
    paymentMethod: selectedPaymentMethod as ExcludeOtherPaymentMethod,
  });

  if (isStripeLoading || !stripe) {
    return <Loader />;
  }
  return (
    <div className="flex flex-col gap-4">
      <Elements
        stripe={stripe}
        options={{
          mode: 'payment',
          amount: Math.round(pricing.priceBreakdown.priceTotal),
          currency: pricing.currencyCode.toLowerCase(),
          paymentMethodTypes: [paymentMethod],
          paymentMethodCreation: 'manual',
          appearance: STRIPE_APPEARANCE,
          locale: pspLocale as StripeElementLocale,
          loader: 'never',
        }}
      >
        <StripeGenericPayContent paymentMethod={paymentMethod} stripe={stripeService.getStripe()} />
      </Elements>
    </div>
  );
};
